import html2canvas from "html2canvas";
import { usePosterContext } from "../../../../context/poster-context";
import Preview2Back from "./components/preview-2-back";
import {
  Preview2AddressWrapper,
  Preview2Body,
  Preview2BodyLeft,
  Preview2BodyRight,
  Preview2Date,
  Preview2Header,
  Preview2HeaderBottom,
  Preview2HeaderTop,
  Preview2ImageWrapper,
  Preview2InfoContent,
  Preview2InfoContentItem,
  Preview2InfoTitle,
  Preview2InfoWrapper,
  Preview2Logo,
  Preview2NoticeContent,
  Preview2NoticeTitle,
  Preview2NoticeWrapper,
  Preview2PrizePoolBox,
  Preview2PrizePoolItem,
  Preview2PrizePoolWrapper,
  Preview2StructureBreak,
  Preview2structureHeaderItem,
  Preview2structureHeaderWrapper,
  Preview2StructureItem,
  Preview2StructureRow,
  Preview2StructureTitle,
  Preview2SubTitle,
  Preview2Title,
  Preview2Wrapper,
} from "./styles/preview-2.style";
import { useEffect, useRef } from "react";
import { toPng } from "html-to-image";
import { pubState } from "../../../../../../recoil/pub.atom";
import { useRecoilValue } from "recoil";
import { PosterInfoType, PosterTab } from "../../../../hooks/use-poster";
import { Preview3PrizePoolWrapper } from "../preview-3/styles/preview-3.style";

const Preview2 = () => {
  const {
    pubName,
    startDate,
    selectedTournament,
    title,
    subTitle,
    // info tab
    infoSection,
    infoFontSize,
    tournamentType,
    registerLimitLevel,
    entry,
    buyIn,
    startingStack,
    reBuyIn,
    reBuyInStack,
    addOn,
    addOnStack,
    duration,
    // address tab
    showAddress,
    address,
    // notice tab
    noticeSection,
    notice,
    // prize pool tab
    prizePoolSection,
    prizePool,
    prizePoolFontSize,
    // structure tab
    structureSection,
    blindStructure,
    showLevel,
    showSmallBlind,
    showBigBlind,
    showAnte,
    showDuration,
    levelLabel,
    smallBlindLabel,
    bigBlindLabel,
    anteLabel,
    durationLabel,
    //util
    isHideLine,
    infoType,
    setInfoType,
    setIsHideLine,
    setTab,
    breakText,
    breakTime,
    // image tab
    image1,
    image2,
    topPadding,
    rightPadding,
  } = usePosterContext();

  const previewRef = useRef<HTMLDivElement>(null);

  const pub = useRecoilValue(pubState);
  useEffect(() => {
    const downloadImage = async () => {
      if (!previewRef.current) return;
      const isHide = isHideLine;

      setIsHideLine(true);

      try {
        // 이미지를 base64로 변환하여 CORS 문제 완전히 회피
        const convertImagesToBase64 = async (): Promise<void> => {
          if (!previewRef.current) return;

          const images = previewRef.current.querySelectorAll("img");
          const conversionPromises: Promise<void>[] = [];

          images.forEach((img) => {
            // blob URL이나 data URL이 아닌 경우만 변환
            if (
              img.src &&
              !img.src.startsWith("blob:") &&
              !img.src.startsWith("data:")
            ) {
              return;
            }

            const promise = new Promise<void>((resolve) => {
              const image = new Image();
              image.crossOrigin = "anonymous";

              image.onload = () => {
                try {
                  const canvas = document.createElement("canvas");
                  canvas.width = image.width;
                  canvas.height = image.height;
                  const ctx = canvas.getContext("2d");
                  if (ctx) {
                    ctx.drawImage(image, 0, 0);
                    const base64 = canvas.toDataURL("image/png");
                    img.src = base64;
                  }
                } catch (e) {
                  console.warn("이미지 변환 실패:", e);
                }
                resolve();
              };

              image.onerror = () => {
                resolve(); // 에러가 나도 진행
              };

              image.src = img.src;
            });

            conversionPromises.push(promise);
          });

          await Promise.all(conversionPromises);
          // 변환 완료 후 렌더링을 위한 대기
          await new Promise((resolve) => setTimeout(resolve, 300));
        };

        await convertImagesToBase64();

        // html-to-image 사용 (box-shadow 지원이 더 좋음)
        let dataUrl: string;

        try {
          dataUrl = await toPng(previewRef.current, {
            cacheBust: true,
            pixelRatio: 1.8,
            backgroundColor: "#ffffff",
            style: {
              transform: "none",
              position: "static",
            },
          });
        } catch (htmlToImageError) {
          // html-to-image 실패 시 html2canvas로 fallback
          console.log(
            "html-to-image 실패, html2canvas 사용:",
            htmlToImageError
          );
          const canvas = await html2canvas(previewRef.current, {
            backgroundColor: "#ffffff",
            scale: 1.8,
            useCORS: true,
            allowTaint: false,
            logging: false,
          });

          // 캔버스를 원하는 크기로 조정
          const resizedCanvas = document.createElement("canvas");
          resizedCanvas.width = 1116;
          resizedCanvas.height = 1576;
          const ctx = resizedCanvas.getContext("2d");
          if (ctx) {
            ctx.drawImage(canvas, 0, 0, 1116, 1576);
            dataUrl = resizedCanvas.toDataURL("image/png");
          } else {
            dataUrl = canvas.toDataURL("image/png");
          }
        }

        const fileName = selectedTournament?.title || "poster";

        // 이미지 저장
        // File System Access API 지원 여부 확인
        if ("showSaveFilePicker" in window) {
          try {
            // dataUrl을 Blob으로 변환
            const response = await fetch(dataUrl);
            const blob = await response.blob();

            // 파일 저장 다이얼로그 표시
            const fileHandle = await (window as any).showSaveFilePicker({
              suggestedName: `${fileName}_preview.png`,
              types: [
                {
                  description: "PNG Image",
                  accept: {
                    "image/png": [".png"],
                  },
                },
              ],
            });

            // 파일 쓰기
            const writable = await fileHandle.createWritable();
            await writable.write(blob);
            await writable.close();
          } catch (saveErr: any) {
            // 사용자가 취소한 경우는 에러로 처리하지 않음
            if (saveErr.name !== "AbortError") {
              console.error("파일 저장 실패:", saveErr);
              // File System Access API 실패 시 기존 방식으로 fallback
              const link = document.createElement("a");
              link.download = `${fileName}_preview.png`;
              link.href = dataUrl;
              link.click();
            }
          }
        } else {
          // File System Access API를 지원하지 않는 브라우저는 기존 방식 사용
          const link = document.createElement("a");
          link.download = `${fileName}_preview.png`;
          link.href = dataUrl;
          link.click();
        }
      } catch (err) {
        console.error("이미지 생성 실패:", err);
        alert("이미지 생성에 실패했습니다.");
      }
      setIsHideLine(isHide);
    };

    // 다운로드 버튼 클릭 이벤트 리스너
    const handleDownloadClick = () => {
      downloadImage();
    };

    window.addEventListener("download-poster-image", handleDownloadClick);

    return () => {
      window.removeEventListener("download-poster-image", handleDownloadClick);
    };
  }, [selectedTournament, isHideLine, setIsHideLine]);

  return (
    <Preview2Wrapper ref={previewRef}>
      <Preview2Back />
      {!image1 && !image2 ? (
        <Preview2Logo
          src="/image-web/poster/poster2/poster2_logo.png"
          alt="logo"
        />
      ) : (
        <Preview2ImageWrapper
          topPadding={topPadding}
          rightPadding={rightPadding}
        >
          {image2 && <img src={image2} alt="image2" />}
          {image1 && <img src={image1} alt="image1" />}
        </Preview2ImageWrapper>
      )}
      <Preview2Header
        isHideLine={isHideLine}
        infoType={infoType === PosterInfoType.MAIN}
        onClick={() => {
          setInfoType(PosterInfoType.MAIN);
          setTab(PosterTab.INFO);
        }}
      >
        <Preview2HeaderTop>
          <img
            src="/image-web/poster/poster2/poster2_pub_logo.png"
            alt="logo"
          />
          <div>{pubName.value}</div>
        </Preview2HeaderTop>
        <Preview2HeaderBottom>
          <Preview2Date color={startDate.color || ""}>
            {startDate.isShow ? startDate.value : ""}
          </Preview2Date>
          <Preview2SubTitle color={subTitle.color || ""}>
            {subTitle.isShow ? subTitle.value : ""}
          </Preview2SubTitle>
          <Preview2Title color={title.color || ""}>
            {title.isShow ? title.value : ""}
          </Preview2Title>
        </Preview2HeaderBottom>
      </Preview2Header>
      <Preview2Body>
        <Preview2BodyLeft showAddress={showAddress}>
          <Preview2InfoWrapper
            isHideLine={isHideLine}
            infoType={infoType === PosterInfoType.INFO}
            onClick={() => {
              setInfoType(PosterInfoType.INFO);
              setTab(PosterTab.INFO);
            }}
          >
            <Preview2InfoTitle>{infoSection}</Preview2InfoTitle>
            <Preview2InfoContent fontSize={infoFontSize.toString()}>
              <Preview2InfoContentItem isShow={tournamentType.isShow}>
                <div>{tournamentType.label}</div>
                <div>{tournamentType.value}</div>
              </Preview2InfoContentItem>
              <Preview2InfoContentItem isShow={registerLimitLevel.isShow}>
                <div>{registerLimitLevel.label}</div>
                <div>{registerLimitLevel.value}</div>
              </Preview2InfoContentItem>
              <Preview2InfoContentItem isShow={entry.isShow}>
                <div>{entry.label}</div>
                <div>{entry.value}</div>
              </Preview2InfoContentItem>
              <Preview2InfoContentItem isShow={buyIn.isShow}>
                <div>{buyIn.label}</div>
                <div>{buyIn.value}</div>
              </Preview2InfoContentItem>
              <Preview2InfoContentItem isShow={startingStack.isShow}>
                <div>{startingStack.label}</div>
                <div>{startingStack.value}</div>
              </Preview2InfoContentItem>
              <Preview2InfoContentItem isShow={reBuyIn.isShow}>
                <div>{reBuyIn.label}</div>
                <div>{reBuyIn.value}</div>
              </Preview2InfoContentItem>
              <Preview2InfoContentItem isShow={reBuyInStack.isShow}>
                <div>{reBuyInStack.label}</div>
                <div>{reBuyInStack.value}</div>
              </Preview2InfoContentItem>
              <Preview2InfoContentItem isShow={addOn.isShow}>
                <div>{addOn.label}</div>
                <div>{addOn.value}</div>
              </Preview2InfoContentItem>
              <Preview2InfoContentItem isShow={addOnStack.isShow}>
                <div>{addOnStack.label}</div>
                <div>{addOnStack.value}</div>
              </Preview2InfoContentItem>
              <Preview2InfoContentItem isShow={duration.isShow}>
                <div>{duration.label}</div>
                <div>{duration.value}</div>
              </Preview2InfoContentItem>
            </Preview2InfoContent>
          </Preview2InfoWrapper>
          <Preview2NoticeWrapper
            isHideLine={isHideLine}
            infoType={infoType === PosterInfoType.NOTICE}
            onClick={() => {
              setInfoType(PosterInfoType.NOTICE);
              setTab(PosterTab.INFO);
            }}
          >
            <Preview2NoticeTitle>{noticeSection}</Preview2NoticeTitle>
            <Preview2NoticeContent>
              <div dangerouslySetInnerHTML={{ __html: notice }} />
            </Preview2NoticeContent>
          </Preview2NoticeWrapper>
          <Preview2PrizePoolWrapper
            isHideLine={isHideLine}
            infoType={infoType === PosterInfoType.PRIZE_POOL}
            onClick={() => {
              setInfoType(PosterInfoType.PRIZE_POOL);
              setTab(PosterTab.INFO);
            }}
          >
            <Preview2NoticeTitle>{prizePoolSection}</Preview2NoticeTitle>
            <Preview2PrizePoolBox>
              {prizePool.map((item, index) => (
                <Preview2PrizePoolItem
                  fontSize={prizePoolFontSize}
                  key={item.rank}
                >
                  <div className={`${index < 3 ? "top3" : ""}`}>
                    {item.rank}
                  </div>
                  <div>{item.prize}</div>
                </Preview2PrizePoolItem>
              ))}
            </Preview2PrizePoolBox>
          </Preview2PrizePoolWrapper>
        </Preview2BodyLeft>
        <Preview2BodyRight
          isHideLine={isHideLine}
          infoType={infoType === PosterInfoType.STRUCTURE}
          showAddress={showAddress}
          onClick={() => {
            setInfoType(PosterInfoType.STRUCTURE);
            setTab(PosterTab.INFO);
          }}
        >
          <Preview2StructureTitle>{structureSection}</Preview2StructureTitle>
          <Preview2structureHeaderWrapper>
            <Preview2structureHeaderItem flex={1} isShow={showLevel}>
              {levelLabel}
            </Preview2structureHeaderItem>
            <Preview2structureHeaderItem flex={2} isShow={showSmallBlind}>
              {smallBlindLabel}
            </Preview2structureHeaderItem>
            <Preview2structureHeaderItem flex={2} isShow={showBigBlind}>
              {bigBlindLabel}
            </Preview2structureHeaderItem>
            <Preview2structureHeaderItem flex={2} isShow={showAnte}>
              {anteLabel}
            </Preview2structureHeaderItem>
            <Preview2structureHeaderItem flex={1} isShow={showDuration}>
              {durationLabel}
            </Preview2structureHeaderItem>
          </Preview2structureHeaderWrapper>
          {blindStructure.map((item, index) => {
            if (item.type === "BREAK") {
              return (
                <Preview2StructureBreak key={`break-${index}`}>
                  {breakText} {item.time} {breakTime}
                </Preview2StructureBreak>
              );
            }
            return (
              <Preview2StructureRow
                hasLine={
                  index !== 0 && blindStructure[index - 1]
                    ? blindStructure[index - 1].type !== "BREAK"
                    : false
                }
                isFinal={index === blindStructure.length - 1}
                key={item.level || `row-${index}`}
              >
                <Preview2StructureItem flex={1} isShow={showLevel}>
                  {item.level}
                </Preview2StructureItem>
                <Preview2StructureItem flex={2} isShow={showSmallBlind}>
                  {item.smallBlind?.toLocaleString()}
                </Preview2StructureItem>
                <Preview2StructureItem flex={2} isShow={showBigBlind}>
                  {item.bigBlind?.toLocaleString()}
                </Preview2StructureItem>
                <Preview2StructureItem flex={2} isShow={showAnte}>
                  {item.ante?.toLocaleString()}
                </Preview2StructureItem>
                <Preview2StructureItem flex={1} isShow={showDuration}>
                  {item.time}
                </Preview2StructureItem>
              </Preview2StructureRow>
            );
          })}
        </Preview2BodyRight>
      </Preview2Body>
      {showAddress && (
        <Preview2AddressWrapper>{address}</Preview2AddressWrapper>
      )}
    </Preview2Wrapper>
  );
};

export default Preview2;
